/**
 * BondsSidebar - Right sidebar displaying bond configuration
 *
 * Shows bonds from the selected configuration.
 * Bond cards display: Year, Calculated Principal, Duration
 */

import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { ConfirmModal } from '~/components/ui/ConfirmModal';
import type { BondSeriesResult } from '~/types/candidate';
import type { BondSeries } from '~/types/inputs';
import { BondCard } from './components/BondCard';

export interface BondsSidebarProps {
  /** List of bonds in current configuration */
  bonds: BondSeries[];

  /** Calculated results for each bond (from best candidate) */
  bondResults?: BondSeriesResult[];

  /** Currently selected bond ID */
  selectedBondId: string | null;

  /** Handler called when a bond is selected (opens modal) */
  onBondSelect: (id: string) => void;

  /** Handler called when Add Bond button is clicked */
  onAddBond: () => void;

  /** Handler called when a bond should be removed */
  onRemoveBond?: (bondId: string) => void;
}

export const BondsSidebar: React.FC<BondsSidebarProps> = ({
  bonds,
  bondResults,
  selectedBondId,
  onBondSelect,
  onAddBond,
  onRemoveBond,
}) => {
  const [bondToRemove, setBondToRemove] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  /**
   * Get calculated principal for a bond from results
   */
  const getCalculatedPrincipal = (bondId: string): number | undefined => {
    if (!bondResults) return undefined;
    const result = bondResults.find((r) => r.bondSeriesId === bondId);
    return result?.totalPrincipal;
  };

  const handleRemoveClick = (bondId: string) => {
    setBondToRemove(bondId);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmRemove = () => {
    if (bondToRemove && onRemoveBond) {
      onRemoveBond(bondToRemove);
    }
    setBondToRemove(null);
    setIsConfirmModalOpen(false);
  };

  return (
    <>
      <Box
        width={LAYOUT_SPACING.SIDEBAR_EXPANDED_SM}
        minWidth={LAYOUT_SPACING.SIDEBAR_EXPANDED_SM}
        height="100%"
        bg="white"
        borderLeft="1px solid"
        borderColor="gray.200"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Box p={3} borderBottom="1px solid" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="semibold" color="gray.700">
            Bonds
          </Text>
        </Box>

        {/* Bond list */}
        <Box
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          p={3}
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E0',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#A0AEC0',
            },
          }}
        >
          <VStack align="stretch" gap={LAYOUT_SPACING.ELEMENT_GAP}>
            {/* Add Bond Button */}
            <Button size="sm" colorPalette="blue" onClick={onAddBond} width="full">
              <HStack gap={2}>
                <Plus size={16} />
                <Text>Add Bond</Text>
              </HStack>
            </Button>

            {bonds.length === 0 ? (
              // Empty state
              <Box
                p={6}
                textAlign="center"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                borderRadius="md"
              >
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  No bonds in this configuration
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Add a bond to get started
                </Text>
              </Box>
            ) : (
              // Bond list
              <VStack align="stretch" gap={2}>
                {bonds.map((bond) => (
                  <BondCard
                    key={bond.id}
                    bond={bond}
                    calculatedPrincipal={getCalculatedPrincipal(bond.id)}
                    isSelected={selectedBondId === bond.id}
                    onSelect={() => onBondSelect(bond.id)}
                    onRemove={onRemoveBond ? () => handleRemoveClick(bond.id) : undefined}
                  />
                ))}
              </VStack>
            )}
          </VStack>
        </Box>
      </Box>

      {/* Confirm Remove Modal */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setBondToRemove(null);
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Bond"
        message={
          bondToRemove
            ? 'Are you sure you want to remove this bond? This will create a new configuration without this bond.'
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </>
  );
};
